import type { ApiFailure, ApiSuccess, PairingStartRequest } from "@kidtube/shared";

import type {
  DeviceCommand,
  DeviceCredential,
  FavoriteMutationResult,
  HeartbeatPayload,
  HomePayload,
  LibraryPayload,
  PairingStartResponse,
  PairingStatusResponse,
  PlaybackPayload,
  PlaybackReport,
  ProfileSummary,
  SearchPayload,
  VideoSummary,
} from "./types";

export class DeviceApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly requestId: string | null;
  readonly details: Readonly<Record<string, unknown>> | undefined;

  constructor(
    status: number,
    code: string,
    message: string,
    requestId: string | null,
    details?: Readonly<Record<string, unknown>>,
  ) {
    super(message);
    this.name = "DeviceApiError";
    this.status = status;
    this.code = code;
    this.requestId = requestId;
    this.details = details;
  }

  isInvalidCredential(): boolean {
    return (
      this.status === 401 &&
      (this.code === "DEVICE_TOKEN_INVALID" || this.code === "DEVICE_AUTH_REQUIRED")
    );
  }
}

export class DeviceApiClient {
  private readonly baseUrl: string;

  constructor(baseUrl: string) {
    this.baseUrl = normalizeHttpUrl(baseUrl);
  }

  startPairing(input: PairingStartRequest): Promise<PairingStartResponse> {
    return this.request<PairingStartResponse>("/api/v1/device/pairing/start", {
      method: "POST",
      body: JSON.stringify(input),
    });
  }

  pairingStatus(pairingId: string, pollingSecret: string): Promise<PairingStatusResponse> {
    return this.request<PairingStatusResponse>(
      `/api/v1/device/pairing/${encodeURIComponent(pairingId)}`,
      {
        method: "GET",
        headers: {
          "x-pairing-secret": pollingSecret,
        },
      },
    );
  }

  getHome(credential: DeviceCredential): Promise<HomePayload> {
    return this.authorized<HomePayload>(credential, "/api/v1/device/home", {
      method: "GET",
    });
  }

  getLibrary(credential: DeviceCredential): Promise<LibraryPayload> {
    return this.authorized<LibraryPayload>(credential, "/api/v1/library", {
      method: "GET",
    });
  }

  getFavorites(credential: DeviceCredential): Promise<VideoSummary[]> {
    return this.authorized<VideoSummary[]>(credential, "/api/v1/favorites", {
      method: "GET",
    });
  }

  addFavorite(
    credential: DeviceCredential,
    contentId: string,
  ): Promise<FavoriteMutationResult> {
    return this.authorized<FavoriteMutationResult>(
      credential,
      `/api/v1/favorites/${encodeURIComponent(contentId)}`,
      { method: "POST" },
    );
  }

  removeFavorite(
    credential: DeviceCredential,
    contentId: string,
  ): Promise<FavoriteMutationResult> {
    return this.authorized<FavoriteMutationResult>(
      credential,
      `/api/v1/favorites/${encodeURIComponent(contentId)}`,
      { method: "DELETE" },
    );
  }

  getProfileSummary(credential: DeviceCredential): Promise<ProfileSummary> {
    return this.authorized<ProfileSummary>(credential, "/api/v1/profile/summary", {
      method: "GET",
    });
  }

  search(
    credential: DeviceCredential,
    query: string,
    cursor: string | null,
  ): Promise<SearchPayload> {
    const parameters = [`q=${encodeURIComponent(query)}`];
    if (cursor !== null && cursor.length > 0) {
      parameters.push(`cursor=${encodeURIComponent(cursor)}`);
    }
    return this.authorized<SearchPayload>(
      credential,
      `/api/v1/device/search?${parameters.join("&")}`,
      { method: "GET" },
    );
  }

  heartbeat(credential: DeviceCredential): Promise<HeartbeatPayload> {
    return this.authorized<HeartbeatPayload>(credential, "/api/v1/device/heartbeat", {
      method: "POST",
      body: "{}",
    });
  }

  reportPlayback(
    credential: DeviceCredential,
    report: PlaybackReport,
  ): Promise<PlaybackPayload> {
    return this.authorized<PlaybackPayload>(credential, "/api/v1/device/playback", {
      method: "POST",
      body: JSON.stringify(report),
    });
  }

  pollCommands(credential: DeviceCredential): Promise<DeviceCommand[]> {
    return this.authorized<DeviceCommand[]>(credential, "/api/v1/device/commands", {
      method: "GET",
    });
  }

  acknowledgeCommand(
    credential: DeviceCredential,
    commandId: string,
    success: boolean,
    result: Readonly<Record<string, unknown>>,
  ): Promise<{ acknowledged: true }> {
    return this.authorized<{ acknowledged: true }>(
      credential,
      `/api/v1/device/commands/${encodeURIComponent(commandId)}/ack`,
      {
        method: "POST",
        body: JSON.stringify({ success, result }),
      },
    );
  }

  private authorized<T>(
    credential: DeviceCredential,
    path: string,
    init: RequestInit,
  ): Promise<T> {
    return this.request<T>(path, init, credential.deviceToken);
  }

  private async request<T>(path: string, init: RequestInit, token?: string): Promise<T> {
    const headers = new Headers(init.headers);
    headers.set("accept", "application/json");
    if (init.body !== undefined && init.body !== null) {
      headers.set("content-type", "application/json");
    }
    if (token !== undefined) {
      headers.set("authorization", `Bearer ${token}`);
    }

    let response: Response;
    try {
      response = await fetch(`${this.baseUrl}${path}`, {
        ...init,
        headers,
        cache: "no-store",
        credentials: "omit",
      });
    } catch {
      throw new DeviceApiError(
        0,
        "NETWORK_ERROR",
        "Không thể kết nối đến KidTube. Hãy kiểm tra mạng.",
        null,
      );
    }

    let payload: unknown;
    try {
      payload = await response.json();
    } catch {
      throw new DeviceApiError(
        response.status,
        "INVALID_RESPONSE",
        "Máy chủ KidTube trả về dữ liệu không hợp lệ.",
        response.headers.get("x-request-id"),
      );
    }

    if (isApiSuccess<T>(payload) && response.ok) {
      return payload.data;
    }

    if (isApiFailure(payload)) {
      throw new DeviceApiError(
        response.status,
        payload.error.code,
        payload.error.message,
        payload.requestId,
        payload.error.details,
      );
    }

    throw new DeviceApiError(
      response.status,
      "INVALID_RESPONSE",
      "Máy chủ KidTube trả về dữ liệu không hợp lệ.",
      response.headers.get("x-request-id"),
    );
  }
}

function normalizeHttpUrl(value: string): string {
  const trimmed = value.trim().replace(/\/+$/, "");
  let parsed: URL;
  try {
    parsed = new URL(trimmed);
  } catch {
    throw new Error("KidTube API URL không hợp lệ.");
  }
  if (parsed.protocol !== "https:" && parsed.protocol !== "http:") {
    throw new Error("KidTube API URL phải dùng HTTP hoặc HTTPS.");
  }
  if (parsed.username !== "" || parsed.password !== "") {
    throw new Error("KidTube API URL không được chứa thông tin đăng nhập.");
  }
  if (parsed.search !== "" || parsed.hash !== "") {
    throw new Error("KidTube API URL không được chứa query hoặc fragment.");
  }
  return trimmed;
}

function isApiSuccess<T>(payload: unknown): payload is ApiSuccess<T> {
  return (
    isRecord(payload) &&
    payload.ok === true &&
    Object.prototype.hasOwnProperty.call(payload, "data") &&
    typeof payload.requestId === "string"
  );
}

function isApiFailure(payload: unknown): payload is ApiFailure {
  if (!isRecord(payload) || payload.ok !== false || !isRecord(payload.error)) {
    return false;
  }
  const details = payload.error.details;
  return (
    typeof payload.requestId === "string" &&
    typeof payload.error.code === "string" &&
    typeof payload.error.message === "string" &&
    (details === undefined || isRecord(details))
  );
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}
